import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Checkbox, CircularProgress, FormControl, FormControlLabel, InputLabel, MenuItem,
  Radio, RadioGroup, Select, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { BackButtonLink } from '../../presentation-components/display-elements-components/back-button-link';
import { SelectableCard } from '../../presentation-components/settings-components/data-import/selectable-card';
import { ReportPeriodContext } from '../../../context/report-periods/report-periods';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import LocationService from '../../../shared/services/location/location.service';
import SubsidiaryService from '../../../shared/services/subsidiary/subsidiary.service';
import { SurveyTemplateService } from '../../../shared/services/survey-template/survey-template.service';
import { IPublishedTemplate } from '../../../shared/services/survey-template/survey-template.model';
import { ICreateSurvey, ISurveyQuestionGroup } from '../../../shared/services/survey/survey.model';
import { ICountry } from '../../../shared/services/location/location.model';
import { ISubsidiary } from '../../../shared/services/subsidiary/subsidiary.model';
import { getSelectableSubsidiaries } from '../../../shared/utils/subsidiary-utils';
import { IconName } from '../../../shared/interfaces/icon-picker.model';

interface Props {
  open: boolean;
  onClose: () => void;
  successHandler: () => void;
  questionGroups: ISurveyQuestionGroup[];
}

const NO_REPORT_PERIOD = '__none__';

// The question survey's questions come from one of three places: a published platform template
// (copied in), a blank set built afterwards, or one of the org's existing question sets.
type QuestionSource = 'template' | 'blank' | 'existing';

/**
 * Two steps: what kind of survey, then the few details that genuinely have to be
 * decided at creation. Everything else (access, questions, recipients) lives on
 * the survey hub afterwards.
 */
export const CreateSurveyDialog = ({ open, onClose, successHandler, questionGroups }: Props) => {
  const navigate = useNavigate();
  const surveyAdminService = SurveyAdminService.getInstance();
  const locationService = useMemo(() => LocationService.getInstance(), []);
  const subsidiaryService = useMemo(() => SubsidiaryService.getInstance(), []);
  const templateService = useMemo(() => new SurveyTemplateService(), []);

  const userNotificationsContext = useContext(UserNotificationsContext);
  const reportPeriodContext = useContext(ReportPeriodContext);

  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countries, setCountries] = useState<ICountry[]>([]);
  const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>([]);
  const [templates, setTemplates] = useState<IPublishedTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState(false);

  const [purpose, setPurpose] = useState<'GENERAL' | 'SUPPLIER_PRODUCT_DATA'>('GENERAL');
  const [name, setName] = useState('');
  const [questionSource, setQuestionSource] = useState<QuestionSource>('blank');
  const [templateId, setTemplateId] = useState('');
  const [surveyQuestionGroupId, setSurveyQuestionGroupId] = useState('');
  const [reportPeriodId, setReportPeriodId] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [subsidiaryId, setSubsidiaryId] = useState('');

  // One effect per open: reset the form, then load the lists. Keyed on `open` only so a
  // context or prop update mid-open cannot re-run the reset and race the template load
  // back to 'blank'. The question-source default is chosen only once the template fetch
  // resolves; until then that section shows a loading state.
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setPurpose('GENERAL');
    setName('');
    setQuestionSource('blank');
    setTemplateId('');
    setSurveyQuestionGroupId(questionGroups[0]?.id ?? '');
    const defaultRp =
      reportPeriodContext.openReportPeriods?.find(p => p.id === reportPeriodContext.lastSelectedOpenReportId)?.id
      ?? reportPeriodContext.lastSelectedReportId
      ?? reportPeriodContext.openReportPeriods?.[0]?.id
      ?? reportPeriodContext.allReportPeriods?.[0]?.id
      ?? '';
    setReportPeriodId(defaultRp);
    setAnonymous(false);
    setSubsidiaryId('');
    setTemplates([]);
    setTemplatesLoading(true);
    setTemplatesError(false);

    let cancelled = false;
    (async () => {
      try {
        const [cList, sList] = await Promise.all([
          locationService.getAllCountries(),
          subsidiaryService.getAll(true),
        ]);
        if (cancelled) return;
        setCountries(cList);
        setSubsidiaries(sList.slice().sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')));
      } catch {
        if (cancelled) return;
        setCountries([]);
        setSubsidiaries([]);
      }
    })();
    (async () => {
      try {
        const tList = await templateService.listPublished();
        if (cancelled) return;
        setTemplates(tList);
        // Offer a template as the default starting point when any are published: a new org can
        // then create a working survey with no pre-work.
        setQuestionSource(tList.length > 0 ? 'template' : 'blank');
      } catch {
        if (cancelled) return;
        setTemplates([]);
        setTemplatesError(true);
        setQuestionSource('blank');
      } finally {
        if (!cancelled) setTemplatesLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isProductData = purpose === 'SUPPLIER_PRODUCT_DATA';
  const chosenSubsidiary = subsidiaries.find(s => s.id === subsidiaryId);
  const derivedCountryName = chosenSubsidiary
    ? (countries.find(c => c.id === chosenSubsidiary.countryId)?.name ?? '')
    : '';

  const reportPeriodOptions = (reportPeriodContext.openReportPeriods?.length
    ? reportPeriodContext.openReportPeriods
    : reportPeriodContext.allReportPeriods ?? [])
    .slice()
    .sort((a, b) => (a.friendlyName ?? '').localeCompare(b.friendlyName ?? ''));

  const questionSourceReady =
    questionSource === 'blank'
    || (questionSource === 'existing' && Boolean(surveyQuestionGroupId))
    || (questionSource === 'template' && Boolean(templateId));

  const stepOneComplete = Boolean(name.trim());
  const stepTwoComplete = isProductData
    ? true
    : Boolean(!templatesLoading && reportPeriodId && subsidiaryId && questionSourceReady);

  const handleSubmit = async () => {
    if (step === 0) {
      if (stepOneComplete) setStep(1);
      return;
    }
    if (!stepTwoComplete) return;
    setIsSubmitting(true);
    try {
      let groupId: string | null = null;
      let templateNote = '';
      if (!isProductData) {
        if (questionSource === 'template') {
          // Copy the template into this org as a new, editable question set, then point the survey
          // at that copy. The template itself is untouched.
          const result = await templateService.instantiate(templateId, name.trim());
          groupId = result.surveyQuestionGroupId;
          if (result.unresolvedFactors.length > 0) {
            templateNote = ` (${result.unresolvedFactors.length} rule(s) need an emission factor chosen)`;
          }
        } else if (questionSource === 'existing') {
          groupId = surveyQuestionGroupId;
        } else {
          const group = await surveyAdminService.createGroup({ name: `${name.trim()} questions` });
          groupId = group.id;
        }
      }
      const payload: ICreateSurvey = {
        name: name.trim(),
        purpose,
        surveyQuestionGroupId: groupId,
        distributionMode: 'INVITED_ONLY',
        allowMultipleSubmissions: false,
        anonymous: isProductData ? false : anonymous,
        ...(isProductData
          ? (reportPeriodId && reportPeriodId !== NO_REPORT_PERIOD ? { reportPeriodId } : {})
          : {
            reportPeriodId,
            subsidiaryId,
            countryId: chosenSubsidiary?.countryId ?? '',
          }),
      };
      const survey = await surveyAdminService.createSurvey(payload);
      userNotificationsContext.openSnackbar({ open: true, message: `Survey created as a draft${templateNote}`, backgroundColor: templateNote ? 'text.primary' : 'primary.dark' });
      successHandler();
      onClose();
      navigate(`/surveys/${survey.id}?tabId=${isProductData ? 'recipients' : 'questions'}`);
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'An error occurred, please try again', backgroundColor: 'text.primary' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title='Create Survey'
      cancelHandler={onClose}
      submitHandler={handleSubmit}
      isSubmitting={isSubmitting}
      disableSaveButton={step === 0 ? !stepOneComplete : !stepTwoComplete}
      submitButtonText={step === 0 ? 'Next' : 'Create survey'}
      maxWidth='sm'
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        {step === 0 && (
          <>
            <Typography variant='body2' color='text.secondary'>
              What should this survey collect?
            </Typography>
            {/* Equal-flex wrappers give both cards the same width, and a definite height for
                the card's fullHeight to resolve against, so the pair always matches. */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <SelectableCard
                  title='Question survey'
                  description='Ask people questions you define, with optional calculations that turn answers into emissions.'
                  icon={IconName.PollOutlined}
                  align='left'
                  selected={purpose === 'GENERAL'}
                  onClick={() => setPurpose('GENERAL')}
                  fullHeight
                />
              </Box>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <SelectableCard
                  title='Product data request'
                  description='Ask register suppliers for per-unit emissions of their products. Figures apply to your product records after review.'
                  icon={IconName.EmailOutlined}
                  align='left'
                  selected={isProductData}
                  onClick={() => setPurpose('SUPPLIER_PRODUCT_DATA')}
                  fullHeight
                />
              </Box>
            </Stack>
            {isProductData && (
              <Typography variant='caption' color='text.secondary'>
                Product data requests are invitation-only with one submission per supplier.
              </Typography>
            )}
            <TextField
              label='Survey name'
              value={name}
              onChange={e => setName(e.target.value)}
              fullWidth
              required
            />
          </>
        )}

        {step === 1 && (
          <>
            <Box sx={{ alignSelf: 'flex-start' }}>
              <BackButtonLink buttonText='Back' onClick={() => setStep(0)} />
            </Box>

            {!isProductData && (
              <>
                <Typography variant='subtitle2' fontWeight={600}>Questions</Typography>
                {templatesLoading && (
                  <Stack direction='row' spacing={1} alignItems='center' sx={{ py: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant='body2' color='text.secondary'>Checking for templates</Typography>
                  </Stack>
                )}
                {!templatesLoading && templatesError && (
                  <Alert severity='warning'>
                    Templates could not be loaded. You can start blank or from an existing question set, or close and reopen this dialog to try again.
                  </Alert>
                )}
                {!templatesLoading && (
                  <RadioGroup value={questionSource} onChange={e => setQuestionSource(e.target.value as QuestionSource)}>
                    <FormControlLabel
                      value='template'
                      control={<Radio size='small' />}
                      label='Start from a template'
                      disabled={templates.length === 0}
                    />
                    <FormControlLabel
                      value='blank'
                      control={<Radio size='small' />}
                      label='Start blank (add questions after creating)'
                    />
                    <FormControlLabel
                      value='existing'
                      control={<Radio size='small' />}
                      label='Start from an existing question set'
                      disabled={questionGroups.length === 0}
                    />
                  </RadioGroup>
                )}
                {!templatesLoading && questionSource === 'template' && (
                  <>
                    <FormControl fullWidth required size='small'>
                      <InputLabel>Template</InputLabel>
                      <Select
                        value={templateId}
                        label='Template'
                        onChange={e => setTemplateId(e.target.value)}
                      >
                        {templates.map(t => (
                          <MenuItem key={t.id} value={t.id}>{t.name} ({t.questionCount} question{t.questionCount === 1 ? '' : 's'})</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant='caption' color='text.secondary'>
                      The template is copied into your account as this survey's question set. You can edit it afterwards, and the template itself is unchanged.
                    </Typography>
                  </>
                )}
                {questionSource === 'existing' && (
                  <FormControl fullWidth size='small'>
                    <InputLabel>Question set</InputLabel>
                    <Select
                      value={surveyQuestionGroupId}
                      label='Question set'
                      onChange={e => setSurveyQuestionGroupId(e.target.value)}
                    >
                      {questionGroups.map(g => (
                        <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {questionSource === 'existing' && (
                  <Typography variant='caption' color='text.secondary'>
                    Question sets can be shared: changes made from any survey using this set apply to all of them.
                  </Typography>
                )}

                <Typography variant='subtitle2' fontWeight={600} sx={{ pt: 1 }}>Reporting</Typography>
                <FormControl fullWidth required size='small'>
                  <InputLabel>Report period</InputLabel>
                  <Select
                    value={reportPeriodId}
                    label='Report period'
                    onChange={e => setReportPeriodId(e.target.value)}
                  >
                    {reportPeriodOptions.map(p => (
                      <MenuItem key={p.id} value={p.id}>{p.friendlyName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth required size='small'>
                  <InputLabel>Subsidiary</InputLabel>
                  <Select
                    value={subsidiaryId}
                    label='Subsidiary'
                    onChange={e => setSubsidiaryId(e.target.value)}
                  >
                    {getSelectableSubsidiaries(subsidiaries, subsidiaryId).map(s => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {derivedCountryName && (
                  <Typography variant='caption' color='text.secondary'>
                    Country: {derivedCountryName} (from the subsidiary)
                  </Typography>
                )}
                <Typography variant='caption' color='text.secondary'>
                  Committed emissions land in this report period and subsidiary. Calculation factor
                  years follow your active report period, the same as manual emissions entry.
                </Typography>

                <Stack direction='row' alignItems='center' spacing={0.5} sx={{ pt: 1 }}>
                  <FormControlLabel
                    control={<Checkbox checked={anonymous} onChange={e => setAnonymous(e.target.checked)} />}
                    label='Anonymous survey'
                  />
                  <Tooltip
                    title='When enabled, respondent names and email addresses are never shown to administrators in the responses view. Respondents are informed of this on the survey page. This setting cannot be changed after the survey is created.'
                    arrow
                  >
                    <InfoOutlined fontSize='small' sx={{ color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Stack>
              </>
            )}

            {isProductData && (
              <>
                <Typography variant='body2' color='text.secondary'>
                  Invited suppliers are asked for the unit, amount and kg CO2e per unit of each
                  product linked to them. Their figures are applied to your product records only
                  after you review them.
                </Typography>
                <FormControl fullWidth size='small'>
                  <InputLabel>Report period (optional)</InputLabel>
                  <Select
                    value={reportPeriodId || NO_REPORT_PERIOD}
                    label='Report period (optional)'
                    onChange={e => setReportPeriodId(e.target.value === NO_REPORT_PERIOD ? '' : e.target.value)}
                  >
                    <MenuItem value={NO_REPORT_PERIOD}><em>None</em></MenuItem>
                    {reportPeriodOptions.map(p => (
                      <MenuItem key={p.id} value={p.id}>{p.friendlyName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant='caption' color='text.secondary'>
                  Linking a report period improves supplier coverage reporting; nothing else depends on it.
                </Typography>
              </>
            )}
          </>
        )}
      </Stack>
    </DialogWrapper>
  );
};
